import * as sessionReplay from "@amplitude/session-replay-browser";
import { RudderAnalytics } from "@rudderstack/analytics-js";

import { addActiveSpanEvent, isInternalEmail } from "@medbillai/utils";

import { trackToLaunchDarkly } from "../flag";
import {
  type AnalyticsConfig,
  type AnalyticsServiceInterface,
  type AttributionTraits,
  defaultConfig,
  defaultSessionReplaySampleRate,
  type EventTypes,
  eventTrackingConfig,
  type ScreenViewProperties,
  type UserTraits,
} from "./types";

class AnalyticsService implements AnalyticsServiceInterface {
  private runInDebugMode = false;
  private initialized = false;
  private rudderAnalytics?: RudderAnalytics;
  private storedAnonymousId?: string;
  private sessionReplayEnabled = false;

  private debugLog(message: string, details: Record<string, unknown>) {
    if (this.runInDebugMode) {
      console.log(`[Analytics]: ${message}`, JSON.stringify(details));
    }
  }

  initialize(config: AnalyticsConfig): Promise<void> {
    if (this.initialized) {
      return Promise.resolve();
    }

    this.rudderAnalytics = new RudderAnalytics();
    this.runInDebugMode = config.debug || false;
    this.rudderAnalytics.load(config.writeKey, config.dataPlaneUrl);

    return new Promise(resolve => {
      this.rudderAnalytics?.ready(() => {
        this.initialized = true;
        this.debugLog("RudderStack initialized", {});

        // Initialize session replay if configured
        if (config.amplitudeApiKey && config.sessionReplay?.enabled) {
          this.initializeSessionReplay(
            config.amplitudeApiKey,
            config.sessionReplay,
          );
        }

        resolve();
      });
    });
  }

  private initializeSessionReplay(
    amplitudeApiKey: string,
    sessionReplayConfig: NonNullable<AnalyticsConfig["sessionReplay"]>,
  ) {
    try {
      const rudderAnonymousId = this.rudderAnalytics?.getAnonymousId();
      // Amplitude requires sessionId to be a number (Unix timestamp in milliseconds)
      // RudderStack's getSessionId() returns a number
      const rudderSessionId = this.rudderAnalytics?.getSessionId();

      if (!rudderSessionId || !rudderAnonymousId) {
        console.warn(
          "Cannot initialize session replay: missing session or device ID",
        );
        return;
      }

      this.debugLog("Initializing session replay", {
        deviceId: rudderAnonymousId,
        sessionId: rudderSessionId,
        sampleRate:
          sessionReplayConfig.sampleRate || defaultSessionReplaySampleRate,
      });

      sessionReplay
        .init(amplitudeApiKey, {
          debugMode: this.runInDebugMode,
          deviceId: rudderAnonymousId,
          sessionId: rudderSessionId,
          sampleRate:
            sessionReplayConfig.sampleRate || defaultSessionReplaySampleRate,
        })
        .promise.then(() => {
          this.sessionReplayEnabled = true;
          this.debugLog("Session replay initialized successfully", {});
        })
        .catch((error: unknown) => {
          console.error("Failed to initialize session replay:", error);
          this.sessionReplayEnabled = false;
        });
    } catch (error) {
      console.error("Error setting up session replay:", error);
      this.sessionReplayEnabled = false;
    }
  }

  private getSessionReplayProperties(): Record<
    string,
    string | number | boolean | null | undefined
  > {
    if (!this.sessionReplayEnabled) {
      return {};
    }

    try {
      const sessionReplayProps = sessionReplay.getSessionReplayProperties();
      // Ensure properties are properly typed for RudderStack
      const typedProps: Record<
        string,
        string | number | boolean | null | undefined
      > = {};

      for (const [key, value] of Object.entries(sessionReplayProps)) {
        if (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean" ||
          value === null ||
          value === undefined
        ) {
          typedProps[key] = value;
        } else {
          // Convert other types to string
          typedProps[key] = String(value);
        }
      }

      return typedProps;
    } catch (error) {
      this.debugLog("Failed to get session replay properties", { error });
      return {};
    }
  }

  trackEvent<E extends keyof EventTypes>(
    eventName: E,
    properties: EventTypes[E],
  ): void {
    this.debugLog("trackEvent", { eventName, properties });
    const config = eventTrackingConfig[eventName] || defaultConfig;
    // Add session replay properties if available
    const sessionReplayProperties = this.getSessionReplayProperties();
    const enhancedProperties = {
      ...properties,
      ...sessionReplayProperties,
    } as EventTypes[E];

    this.rudderAnalytics?.track(eventName, enhancedProperties, config);

    // Also track to LaunchDarkly for experimentation metrics
    trackToLaunchDarkly({
      metricKey: eventName,
      data: properties,
    });

    // best effort to add to otel span
    try {
      addActiveSpanEvent(
        eventName,
        properties as unknown as Parameters<typeof addActiveSpanEvent>[1],
      );
    } catch {
      // this is only best effort, so ignore errors
    }
  }

  identifyUser(userId: string, traits: UserTraits): void {
    if (!traits.email) {
      console.warn("Identifying user without email", { userId, traits });
    }

    // Mark internal users
    const enhancedTraits = {
      ...traits,
      isInternal: traits.email ? isInternalEmail(traits.email) : undefined,
    };

    this.debugLog("identifyUser", { userId, traits: enhancedTraits });
    const options = this.storedAnonymousId
      ? { anonymousId: this.storedAnonymousId }
      : undefined;
    this.rudderAnalytics?.identify(userId, enhancedTraits, options);
  }

  identifyAnonymousUser(traits: AttributionTraits): void {
    if (Object.keys(traits).length === 0) {
      return;
    }
    this.debugLog("identifyAnonymousUser", { traits });
    this.rudderAnalytics?.identify(
      this.rudderAnalytics.getUserId() ??
        this.storedAnonymousId ??
        this.rudderAnalytics.getAnonymousId(),
      traits,
    );
  }

  resetUser(): void {
    this.debugLog("Reset user for analytics", {});
    this.rudderAnalytics?.reset();
  }

  setAnonymousId(anonymousId: string): void {
    this.debugLog("setAnonymousId", { anonymousId });
    this.rudderAnalytics?.setAnonymousId(anonymousId);
    this.storedAnonymousId = anonymousId;
  }

  trackScreenView(screenName: string, properties: ScreenViewProperties): void {
    this.debugLog("trackScreenView", { screenName, properties });

    // Add session replay properties if available
    const sessionReplayProperties = this.getSessionReplayProperties();
    const enhancedProperties = {
      ...properties,
      ...sessionReplayProperties,
    };

    this.rudderAnalytics?.page(screenName, enhancedProperties, defaultConfig);

    // Also track to LaunchDarkly for experimentation metrics
    trackToLaunchDarkly({
      metricKey: `ScreenView_${screenName}`,
      data: properties,
    });
  }
}

const analyticsService = new AnalyticsService();
export { analyticsService };
